import React, { useState, useEffect } from 'react';
import ChatbotAPI from './ChatbotAPI';
import { ErrorToastContainer } from '../../../function/CustomFunction';

export default function ChatbotAnalytics() {
  const [analytics, setAnalytics] = useState(null);
  const [flows, setFlows] = useState([]);
  const [selectedFlow, setSelectedFlow] = useState('all');
  const [dateRange, setDateRange] = useState('7d'); // 7d, 30d, 90d, custom
  const [isLoading, setIsLoading] = useState(false);
  const [customDateRange, setCustomDateRange] = useState({
    start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  // Load analytics data
  const loadAnalytics = async () => {
    setIsLoading(true);
    try {
      let params = {};
      
      if (selectedFlow !== 'all') {
        params.flow_id = selectedFlow;
      }
      
      if (dateRange === 'custom') {
        params.start_date = customDateRange.start;
        params.end_date = customDateRange.end;
      } else {
        const endDate = new Date();
        const startDate = new Date();
        
        switch (dateRange) {
          case '7d':
            startDate.setDate(endDate.getDate() - 7);
            break;
          case '30d':
            startDate.setDate(endDate.getDate() - 30);
            break;
          case '90d':
            startDate.setDate(endDate.getDate() - 90);
            break;
        }
        
        params.start_date = startDate.toISOString().split('T')[0];
        params.end_date = endDate.toISOString().split('T')[0];
      }

      const data = await ChatbotAPI.getAnalytics(params.flow_id, params);
      setAnalytics(data);
    } catch (error) {
      ErrorToastContainer('Failed to load analytics');
      console.error('Error loading analytics:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Load flows for filter
  const loadFlows = async () => {
    try {
      const flowsData = await ChatbotAPI.loadFlows();
      setFlows(flowsData);
    } catch (error) {
      console.error('Error loading flows:', error);
    }
  };

  useEffect(() => {
    loadFlows();
  }, []);

  useEffect(() => {
    loadAnalytics();
  }, [selectedFlow, dateRange, customDateRange]);

  // Format number with commas
  const formatNumber = (num) => {
    return num?.toLocaleString() || '0';
  };

  // Format percentage
  const formatPercentage = (num) => {
    return num ? `${(num * 100).toFixed(1)}%` : '0%';
  };

  // Get date range label
  const getDateRangeLabel = () => {
    switch (dateRange) {
      case '7d':
        return 'Last 7 days';
      case '30d':
        return 'Last 30 days';
      case '90d':
        return 'Last 90 days';
      case 'custom':
        return `${customDateRange.start} to ${customDateRange.end}`;
      default:
        return 'Last 7 days';
    }
  };

  // Mock data for demonstration (replace with real data from API)
  const mockAnalytics = {
    totalConversations: 1247,
    activeConversations: 23,
    completedConversations: 1224,
    averageResponseTime: 2.3,
    userSatisfaction: 0.87,
    conversionRate: 0.34,
    totalMessages: 8923,
    botMessages: 4567,
    userMessages: 4356,
    flows: [
      { name: 'Customer Support', conversations: 456, completionRate: 0.89, avgTime: 3.2 },
      { name: 'Product Inquiry', conversations: 234, completionRate: 0.76, avgTime: 2.1 },
      { name: 'Order Status', conversations: 189, completionRate: 0.92, avgTime: 1.8 },
      { name: 'General Info', conversations: 368, completionRate: 0.81, avgTime: 2.5 }
    ],
    dailyStats: [
      { date: '2024-01-01', conversations: 45, messages: 234 },
      { date: '2024-01-02', conversations: 52, messages: 289 },
      { date: '2024-01-03', conversations: 38, messages: 198 },
      { date: '2024-01-04', conversations: 67, messages: 345 },
      { date: '2024-01-05', conversations: 41, messages: 223 },
      { date: '2024-01-06', conversations: 29, messages: 156 },
      { date: '2024-01-07', conversations: 35, messages: 187 }
    ]
  };

  const data = analytics || mockAnalytics;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ 
        background: '#fff', 
        borderBottom: '1px solid #e0e0e0', 
        padding: '20px 24px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>ChatBot Analytics</h1>
            <div style={{ fontSize: 14, color: '#666', marginTop: 4 }}>
              {getDateRangeLabel()} • {selectedFlow === 'all' ? 'All Flows' : flows.find(f => f.id === selectedFlow)?.name}
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            {/* Flow Filter */}
            <select
              value={selectedFlow}
              onChange={(e) => setSelectedFlow(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: 4,
                fontSize: 14
              }}
            >
              <option value="all">All Flows</option>
              {flows.map(flow => (
                <option key={flow.id} value={flow.id}>{flow.name}</option>
              ))}
            </select>

            {/* Date Range Filter */}
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: 4,
                fontSize: 14
              }}
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="custom">Custom range</option>
            </select>

            {/* Custom Date Range */}
            {dateRange === 'custom' && (
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="date"
                  value={customDateRange.start}
                  onChange={(e) => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: 4,
                    fontSize: 14
                  }}
                />
                <input
                  type="date"
                  value={customDateRange.end}
                  onChange={(e) => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: 4,
                    fontSize: 14
                  }}
                />
              </div>
            )}

            <button
              onClick={loadAnalytics}
              disabled={isLoading}
              style={{
                padding: '8px 16px',
                background: '#25d366',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.6 : 1
              }}
            >
              {isLoading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      {/* Analytics Content */}
      <div style={{ flex: 1, padding: '24px', overflowY: 'auto' }}>
        {isLoading ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%',
            fontSize: 16,
            color: '#666'
          }}>
            Loading analytics...
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {/* Key Metrics */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 16 }}>
              <div style={{ 
                background: '#fff', 
                border: '1px solid #e0e0e0', 
                borderRadius: 8, 
                padding: 20,
                textAlign: 'center'
              }}>
                <div style={{ fontSize: 32, fontWeight: 600, color: '#25d366', marginBottom: 8 }}>
                  {formatNumber(data.totalConversations)}
                </div>
                <div style={{ fontSize: 14, color: '#666' }}>Total Conversations</div>
              </div>

              <div style={{ 
                background: '#fff', 
                border: '1px solid #e0e0e0', 
                borderRadius: 8, 
                padding: 20,
                textAlign: 'center'
              }}>
                <div style={{ fontSize: 32, fontWeight: 600, color: '#128C7E', marginBottom: 8 }}>
                  {formatNumber(data.activeConversations)}
                </div>
                <div style={{ fontSize: 14, color: '#666' }}>Active Conversations</div>
              </div>

              <div style={{ 
                background: '#fff', 
                border: '1px solid #e0e0e0', 
                borderRadius: 8, 
                padding: 20,
                textAlign: 'center'
              }}>
                <div style={{ fontSize: 32, fontWeight: 600, color: '#FF6B35', marginBottom: 8 }}>
                  {data.averageResponseTime}s
                </div>
                <div style={{ fontSize: 14, color: '#666' }}>Avg Response Time</div>
              </div>

              <div style={{ 
                background: '#fff', 
                border: '1px solid #e0e0e0', 
                borderRadius: 8, 
                padding: 20,
                textAlign: 'center'
              }}>
                <div style={{ fontSize: 32, fontWeight: 600, color: '#4CAF50', marginBottom: 8 }}>
                  {formatPercentage(data.userSatisfaction)}
                </div>
                <div style={{ fontSize: 14, color: '#666' }}>User Satisfaction</div>
              </div>
            </div>

            {/* Message Statistics */}
            <div style={{ 
              background: '#fff', 
              border: '1px solid #e0e0e0', 
              borderRadius: 8, 
              padding: 20
            }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: 18, fontWeight: 600 }}>Message Statistics</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 600, color: '#25d366' }}>
                    {formatNumber(data.totalMessages)}
                  </div>
                  <div style={{ fontSize: 14, color: '#666' }}>Total Messages</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 600, color: '#128C7E' }}>
                    {formatNumber(data.botMessages)}
                  </div>
                  <div style={{ fontSize: 14, color: '#666' }}>Bot Messages</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 600, color: '#FF6B35' }}>
                    {formatNumber(data.userMessages)}
                  </div>
                  <div style={{ fontSize: 14, color: '#666' }}>User Messages</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 600, color: '#4CAF50' }}>
                    {formatPercentage(data.conversionRate)}
                  </div>
                  <div style={{ fontSize: 14, color: '#666' }}>Conversion Rate</div>
                </div>
              </div>
            </div>

            {/* Flow Performance */}
            <div style={{ 
              background: '#fff', 
              border: '1px solid #e0e0e0', 
              borderRadius: 8, 
              padding: 20
            }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: 18, fontWeight: 600 }}>Flow Performance</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Flow Name</th>
                      <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600 }}>Conversations</th>
                      <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600 }}>Completion Rate</th>
                      <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600 }}>Avg Time (min)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.flows.map((flow, index) => (
                      <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '12px', fontWeight: 500 }}>{flow.name}</td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>{formatNumber(flow.conversations)}</td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>{formatPercentage(flow.completionRate)}</td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>{flow.avgTime}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Daily Trends */}
            <div style={{ 
              background: '#fff', 
              border: '1px solid #e0e0e0', 
              borderRadius: 8, 
              padding: 20
            }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: 18, fontWeight: 600 }}>Daily Trends</h3>
              <div style={{ display: 'flex', gap: 16, overflowX: 'auto', paddingBottom: 8 }}>
                {data.dailyStats.map((day, index) => (
                  <div key={index} style={{ 
                    minWidth: 120, 
                    textAlign: 'center',
                    padding: '12px',
                    background: '#f8f9fa',
                    borderRadius: 6
                  }}>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                      {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 600, color: '#25d366' }}>
                      {day.conversations}
                    </div>
                    <div style={{ fontSize: 12, color: '#666' }}>
                      {day.messages} messages
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Insights */}
            <div style={{ 
              background: '#fff', 
              border: '1px solid #e0e0e0', 
              borderRadius: 8, 
              padding: 20
            }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: 18, fontWeight: 600 }}>Key Insights</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
                <div style={{ 
                  padding: '16px', 
                  background: '#e8f5e8', 
                  borderRadius: 6,
                  borderLeft: '4px solid #25d366'
                }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>✅ High Performance</div>
                  <div style={{ fontSize: 14, color: '#666' }}>
                    Your chatbot has a {formatPercentage(data.userSatisfaction)} satisfaction rate, 
                    which is above industry average.
                  </div>
                </div>
                
                <div style={{ 
                  padding: '16px', 
                  background: '#fff3e0', 
                  borderRadius: 6,
                  borderLeft: '4px solid #FF6B35'
                }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>⚡ Fast Response</div>
                  <div style={{ fontSize: 14, color: '#666' }}>
                    Average response time of {data.averageResponseTime} seconds, 
                    ensuring quick customer support.
                  </div>
                </div>
                
                <div style={{ 
                  padding: '16px', 
                  background: '#e3f2fd', 
                  borderRadius: 6,
                  borderLeft: '4px solid #128C7E'
                }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>📈 Growth Opportunity</div>
                  <div style={{ fontSize: 14, color: '#666' }}>
                    {formatPercentage(data.conversionRate)} conversion rate shows 
                    potential for optimization.
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 